import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { SafeAreaView } from "react-native-safe-area-context";
import { CircleHelp } from "lucide-react-native";
import { FocusablePressable } from "../components/FocusablePressable";
import { RootStackParamList } from "../navigation/types";
import { colors, radius, spacing, systemSansFont } from "../theme/tokens";

type Props = NativeStackScreenProps<RootStackParamList, "NotFound">;

export function NotFoundScreen({ navigation }: Props) {
  const { width } = useReferenceWindowDimensions();
  const compact = width < 700;
  return (
    <SafeAreaView style={styles.safe} edges={["top", "bottom"]}>
      <View style={[styles.content, compact && styles.contentCompact]}>
        <View style={styles.iconFrame}>
          <CircleHelp color={colors.textMuted} size={28} />
        </View>
        <Text style={[styles.title, compact && styles.titleCompact]}>
          Page Not Found
        </Text>
        <Text style={styles.copy}>This page does not exist.</Text>
        <FocusablePressable
          onPress={() =>
            navigation.reset({ index: 0, routes: [{ name: "Home" }] })
          }
          style={(focused, pressed, hovered) => [
            styles.homeButton,
            (focused || hovered) && styles.homeButtonActive,
            pressed && styles.pressed,
          ]}
        >
          <Text style={styles.homeButtonText}>Go to Home Page</Text>
        </FocusablePressable>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 40,
    paddingVertical: 32,
  },
  contentCompact: { paddingHorizontal: 20 },
  iconFrame: {
    width: 52,
    height: 52,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 26,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
    backgroundColor: "rgba(255,255,255,0.06)",
  },
  title: {
    marginTop: spacing.sm,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: "900",
    textAlign: "center",
  },
  titleCompact: { fontSize: 22, lineHeight: 28 },
  copy: {
    maxWidth: 560,
    marginTop: spacing.md,
    color: colors.textMuted,
    fontFamily: systemSansFont,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: "600",
    textAlign: "center",
  },
  homeButton: {
    minHeight: 48,
    marginTop: 28,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radius.small,
    borderWidth: 2,
    borderColor: "transparent",
    backgroundColor: colors.text,
  },
  homeButtonActive: {
    borderColor: colors.brandBright,
    transform: [{ scale: 1.04 }],
  },
  homeButtonText: {
    color: "#111",
    fontFamily: systemSansFont,
    fontSize: 14,
    fontWeight: "900",
  },
  pressed: { opacity: 0.82 },
});
